import React, { useEffect, useState } from "react";
import Select, { components } from "react-select";
import useDebounce from "../../../hooks/useDebounce";
import SearchIcon from '@material-ui/icons/Search';
import { useSelector } from "react-redux";
import { getAiAssistantLoading } from "../../../store/global/selectors";
import { Tooltip } from "@material-ui/core";

// Custom Option (to show pill inside dropdown)
const CustomOption = (props) => (
  <components.Option {...props}>
    <div style={{ display: "flex", alignItems: "center", cursor: "pointer", padding: 0, justifyContent: "space-between", gap: "6px" }}>
      <span>{props.data.label}</span>
      {props.data.shared && (
        <span
          style={{
            background: "#e6f0ff",
            color: "#0066cc",
            fontSize: "11px",
            padding: "2px 6px",
            borderRadius: "12px",
          }}
        >
          shared
        </span>
      )}
    </div>
  </components.Option>
);

const DropdownIndicator = (props) => (
  <components.DropdownIndicator {...props}>
    <SearchIcon />
  </components.DropdownIndicator>
);

export default function BlueprintSelect({ allBlueprints, onSelectBlueprint, selectedBlueprint }) {

  const isAiAssistantProgressing = useSelector(getAiAssistantLoading);

  const [options, setOptions] = useState([])
  const [defaultValue, setDefaultValue] = useState(null)
  const [search, setSearch] = useState("")
  const debouncedSearch = useDebounce(search, 300)

  useEffect(() => {
    if (allBlueprints?.length > 0 && selectedBlueprint) {
      const data = {
        label: "Recently used blueprints"
      }

      if (debouncedSearch && debouncedSearch !== "") {
        data.label = `Search result "${debouncedSearch}"`
        data.options = allBlueprints
          .filter(item => item.name.toLowerCase().includes(debouncedSearch.toLowerCase()) && item._id !== selectedBlueprint._id)
          .slice(0, 5).map((item) => ({
            label: item.name,
            value: item._id,
            slug: item.slug,
            shared: item?.bpOwner ? true : false
          }))
      } else {
        data.options = allBlueprints
          ?.filter(item => item._id !== selectedBlueprint._id)
          ?.slice(0, 5).map((item) => ({
            label: item.name,
            value: item._id,
            slug: item.slug,
            shared: item?.bpOwner ? true : false
          }))
      }

      data.options = [{
        label: selectedBlueprint.name,
        value: selectedBlueprint._id,
        slug: selectedBlueprint.slug,
        shared: selectedBlueprint?.bpOwner ? true : false
      }, ...data.options]

      setOptions([data])
    }
  }, [allBlueprints, debouncedSearch, selectedBlueprint])

  useEffect(() => {
    if (options?.length > 0) {
      setDefaultValue(options[0]?.options?.[0])
    }
  }, [options])

  return (
    <Tooltip
      title={isAiAssistantProgressing ? "AI assistant in progress. Please wait." : ""}
      arrow
    >
      <span>
        <Select
          inputId="blueprint-search-bar"
          classNamePrefix="blueprint"
          menuPortalTarget={null}
          value={defaultValue}
          options={options}
          components={{ Option: CustomOption, DropdownIndicator }}
          onInputChange={(val) => setSearch(val)}
          onChange={(val) => onSelectBlueprint(null, val)}
          isSearchable
          isDisabled={isAiAssistantProgressing}
          placeholder="Select a blueprint..."
          styles={{
            container: (provided) => ({
              ...provided,
              cursor: 'pointer',
            }),
            control: (provided) => ({
              ...provided,
              cursor: 'pointer',
            }),
            menu: (provided) => ({
              ...provided,
              zIndex: 9999,
              width: "max-content",
              fontSize: "12px",
              cursor: 'pointer'
            }),
            menuList: (provided) => ({
              ...provided,
              paddingTop: 0,
              paddingBottom: 0,
              cursor: 'pointer'
            }),
            option: (provided, state) => ({
              ...provided,
              padding: "4px 8px",       // compact padding
              fontSize: "12px",
              lineHeight: "1.2",
              minHeight: "unset",
              cursor: 'pointer'
            }),
            input: (styles) => ({ ...styles, minWidth: '8rem', cursor: 'pointer' }),
            groupHeading: (styles) => ({ ...styles, textTransform: "none", fontWeight: 500, fontSize: "13px" })
          }}
          getOptionProps={(option, { index }) => ({
            id: `blueprint-option-${index}`, // unique ID for each option
            'data-testid': `blueprint-option-${option.value}`, // stable locator
          })}

          classNames={{
            menu: () => "blueprint-menu-list",
            option: () => "blueprint-menu-item",
          }}
        />
      </span>
    </Tooltip>
  );
}
